import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Field, Select } from '@/components/ui/Field';
import type { Line, PlacedItem } from './types';
import { useDraggableDialog } from './useDraggableDialog';

interface DoubleSwitchDialogProps {
  open: boolean;
  item: PlacedItem | null;
  lines: Line[];
  onChangeLine: (itemId: string, side: 'left' | 'right', lineId: string | null) => void;
  onToggleLine: (lineId: string) => void;
  onClose: () => void;
}

type DialogMode = 'control' | 'settings';
type SwitchSide = 'left' | 'right';

export function DoubleSwitchDialog({
  open,
  item,
  lines,
  onChangeLine,
  onToggleLine,
  onClose,
}: DoubleSwitchDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<DialogMode>('control');
  const { overlayRef, panelRef, panelStyle, dragHandleProps } = useDraggableDialog(open, { initialPlacement: 'right' });

  useEffect(() => {
    if (open) setMode('control');
  }, [item?.id, open]);
  const lineNameById = useMemo(() => new Map(lines.map(line => [line.id, line.name])), [lines]);

  if (!open || !item) return null;

  const leftLineId = item.switchLines?.left ?? '';
  const rightLineId = item.switchLines?.right ?? '';
  const lineLabel = (lineId: string) => lineNameById.get(lineId) ?? t('floor_plan.double_switch_unassigned');
  const handleChange = (side: 'left' | 'right', value: string) => {
    onChangeLine(item.id, side, value || null);
  };
  const renderControlButton = (side: SwitchSide, lineId: string) => {
    const assigned = Boolean(lineId);
    const badge = side === 'left' ? 'L' : 'R';
    return (
      <div className="rounded-md border border-border/60 bg-surface p-2">
        <Button
          type="button"
          variant="ghost"
          className="w-full justify-between whitespace-normal border-border/60 bg-surface-2 text-left hover:bg-surface-3"
          disabled={!assigned}
          onClick={() => {
            if (lineId) onToggleLine(lineId);
          }}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-border-2 bg-surface font-mono text-[11px] text-ink-3">
              {badge}
            </span>
            <span>{side === 'left' ? t('floor_plan.double_switch_left') : t('floor_plan.double_switch_right')}</span>
          </span>
          <span className="min-w-0 truncate pl-2 text-right font-mono text-[11px] text-ink-4">
            {assigned ? lineLabel(lineId) : t('floor_plan.double_switch_unassigned')}
          </span>
        </Button>
      </div>
    );
  };

  return (
    <div ref={overlayRef} className="pointer-events-none absolute inset-0 z-20">
      <div
        ref={panelRef}
        style={panelStyle}
        className="pointer-events-auto absolute w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-border bg-surface shadow-xl"
      >
        <div
          className="cursor-grab border-b border-border bg-surface-2 px-4 py-3 active:cursor-grabbing"
          {...dragHandleProps}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-ink">{t('floor_plan.double_switch_title')}</div>
              <div className="mt-1 text-xs leading-relaxed text-ink-4">
                {mode === 'control' ? t('floor_plan.double_switch_control_hint') : t('floor_plan.double_switch_hint')}
              </div>
            </div>
            <div className="mt-0.5 flex gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
            </div>
          </div>
        </div>

        <div className="p-4">
          {mode === 'control' ? (
            <div className="rounded-md border border-border/60 bg-surface-2 p-3">
              <div className="mb-2 text-[11px] font-medium uppercase text-ink-4">
                {t('floor_plan.double_switch_control')}
              </div>
              <div className="space-y-2">
                {renderControlButton('left', leftLineId)}
                {renderControlButton('right', rightLineId)}
              </div>
            </div>
          ) : (
            <div className="space-y-3 rounded-md border border-border/60 bg-surface-2 p-3">
              <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] items-end gap-2">
                <span className="mb-1 flex h-6 w-6 items-center justify-center rounded border border-border-2 bg-surface font-mono text-[11px] text-ink-3">L</span>
                <Field label={t('floor_plan.double_switch_left')}>
                  <Select value={leftLineId} onChange={event => handleChange('left', event.target.value)}>
                    <option value="">{t('floor_plan.double_switch_unassigned')}</option>
                    {lines.map(line => (
                      <option key={line.id} value={line.id}>{line.name}</option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] items-end gap-2">
                <span className="mb-1 flex h-6 w-6 items-center justify-center rounded border border-border-2 bg-surface font-mono text-[11px] text-ink-3">R</span>
                <Field label={t('floor_plan.double_switch_right')}>
                  <Select value={rightLineId} onChange={event => handleChange('right', event.target.value)}>
                    <option value="">{t('floor_plan.double_switch_unassigned')}</option>
                    {lines.map(line => (
                      <option key={line.id} value={line.id}>{line.name}</option>
                    ))}
                  </Select>
                </Field>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-border bg-surface-2 px-4 py-3">
          {mode === 'control' ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => setMode('settings')}>
              {t('floor_plan.double_switch_configure')}
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={() => onChangeLine(item.id, 'left', null)}>
                {t('floor_plan.double_switch_clear_left')}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => onChangeLine(item.id, 'right', null)}>
                {t('floor_plan.double_switch_clear_right')}
              </Button>
            </>
          )}
          <Button type="button" size="sm" onClick={onClose}>
            {t('floor_plan.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
